"use client";

import React, { useState } from "react";
import { TabTray, type TabTraySize } from "../../atoms/tab-tray/tab-tray";
import { HpTabCarouselSubheading } from "./subheading.client";
import styles from "./hp-tab-carousel.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * HpTabCarouselShell — the tab-switch shell, and the ONLY client JavaScript HP
 * Tab Carousel ships at the section level. It holds the selected tab and shows
 * the matching panel.
 *
 * ⚠ The header and every panel arrive ALREADY RENDERED from the Server
 * Component, so nothing here re-renders their content: the shell only swaps
 * WHICH panel is visible. Every panel stays mounted and the inactive ones carry
 * the native `hidden` attribute, which drops them from layout, the tab order
 * and the accessibility tree — no ARIA of our own (the first rule of ARIA).
 *
 * WHY THIS IS A CLIENT COMPONENT AT ALL: "one panel showing at a time, chosen
 * from a tray" has no zero-JavaScript form — there is no server-rendered
 * version of the selection to protect. That is one of the four accepted
 * arguments for a boundary; the choice is recorded in
 * design-conflicts/hp-tab-carousel.mdx. The boundary is kept at this leaf: the
 * header and the composed bodies below stay on the server.
 *
 * ⚠ The tab tray fires its OWN `link_click` on selection (see TabTray). This
 * shell must NOT fire another on top — `onChange` only moves state.
 *
 * ⚠ The subhead is composed HERE, not handed in already rendered like the
 * headline, because its emphasised phrases are interactive: a <strong> naming a
 * tab is a switch button driving the same `activeValue` the tray drives, so it
 * needs this shell's state. Only its HTML crosses the boundary (a string); the
 * headline still arrives as a server node. See `HpTabCarouselSubheading`.
 */
export interface HpTabCarouselShellProps {
  /** The headline, already rendered on the server. Null for a blank headline. */
  heading?: React.ReactNode;
  /** The subhead's CMS markup, composed into an interactive subhead here (see the
   *  header note above). Blank or absent drops the subhead. */
  subheadingContent?: string;
  /** The section's background glow slots, index-aligned with `items`/`panels`.
   *  Only the ACTIVE tab's is rendered, so the glow shows only while that tab is
   *  selected; a tab that does not opt in passes `null`. Each is already rendered
   *  on the server, so no glow SVG enters the client bundle. The shown one sits
   *  behind all content as the first child of the section. */
  backdrops?: React.ReactNode[];
  /** Renders the tab tray. Off, only the first panel shows. */
  tabs: boolean;
  /** Accessible name for the tab tray. */
  tabsLabel: string;
  /** The tab tray's height treatment. Absent, the tray renders medium. */
  tabSize?: TabTraySize;
  /** The tabs, index-aligned with `panels`: each `value` and its wording. */
  items: { value: string; label: string }[];
  /** The panel bodies, already rendered, index-aligned with `items`. */
  panels: React.ReactNode[];
  /** Optional already-rendered node shown between the tray and the panels — the
   *  frame's "Based on your location …" line. Absent, nothing renders there. */
  locationSlot?: React.ReactNode;
  /** The section's default legal disclaimer, shown centered at the bottom when
   *  there are no panels (nothing to switch between). Absent, nothing renders. */
  legal?: React.ReactNode;
  /** Per-tab legal disclaimers, index-aligned with `items`/`panels`. Only the
   *  ACTIVE tab's is rendered, so the disclaimer swaps with the selected tab; a
   *  tab with no legal of its own inherits the section default (resolved on the
   *  server). Each is already rendered, so its copy never enters the client
   *  bundle. */
  legals?: React.ReactNode[];
  /** Where the section sits, carried into the tab tray's own click report. */
  linkPosition?: string;
  /** Anchor id for the section. */
  id?: string;
}

function HpTabCarouselShellImpl({
  heading,
  subheadingContent,
  backdrops,
  tabs,
  tabsLabel,
  tabSize,
  items,
  panels,
  locationSlot,
  legal,
  legals,
  linkPosition,
  id
}: HpTabCarouselShellProps) {
  /* One panel has nothing to switch between, so the tray, its name and the
     whole selection drop with it. */
  const showTray = tabs && items.length > 1;

  /* THE ONLY STATE IN HP TAB CAROUSEL's SECTION SHELL. Seeded from the first tab
     so the tray is controlled from the very first render — TabTray latches
     controlled-ness at mount, and a value arriving late would be ignored. */
  const [activeValue, setActiveValue] = useState(() => items[0]?.value ?? "");

  /* With no tray there is nothing to switch to, so the first panel is the only
     one that can ever show — pin it rather than trust a value that never
     moves. A value that names no tab (a reordered list) falls back to the
     first. */
  const activeIndex = showTray ?
    Math.max(items.findIndex(item => item.value === activeValue), 0) :
    0;

  /* The headline/subhead block. The headline crosses as an already-rendered
     server node; the subhead is composed here because its phrases switch tabs.
     The switch set is the tabs ONLY when the tray shows — with no tray (or one
     panel) `activeIndex` is pinned to 0 and a switch would be a dead control, so
     every phrase stays plain emphasis. The active value is read off `activeIndex`,
     not raw `activeValue`, so the pressed phrase tracks the tab the tray displays
     even if a stale value named no tab. */
  const headerText =
    heading || subheadingContent ? (
      <div className={styles.headerText}>
        {heading}
        {subheadingContent ? (
          <HpTabCarouselSubheading
            activeValue={items[activeIndex]?.value ?? ""}
            content={subheadingContent}
            items={showTray ? items : []}
            linkPosition={linkPosition}
            onSelect={setActiveValue}
          />
        ) : null}
      </div>
    ) : null;

  if (panels.length === 0) {
    /* No panels means no active tab, so no glow — the backdrop is gated on the
       selected tab below and there is none here. */
    return headerText || legal ? (
      <section className={styles.root} id={id || undefined}>
        {headerText ? <div className={styles.header}>{headerText}</div> : null}
        {legal ? <div className={styles.legal}>{legal}</div> : null}
      </section>
    ) : null;
  }

  return (
    <section className={styles.root} id={id || undefined}>
      {/* Only the ACTIVE tab's glow — null for a tab that did not opt in — so the
          glow appears only while the tab carrying it (Streaming) is selected. */}
      {backdrops?.[activeIndex] ?? null}
      {(headerText || showTray) && (
        <div className={styles.header}>
          {headerText}

          {showTray && (
            <div className={styles.tabs}>
              <TabTray
                ariaLabel={tabsLabel}
                /* Below 735px the tray is full-width; fill stretches the tabs
                   across it rather than leaving the pill's fill empty on the
                   right (WAT-12516). design-conflicts/tab-tray.mdx. */
                fill
                items={items}
                linkPosition={linkPosition}
                /* Moves state only — TabTray already reported the click. */
                onChange={setActiveValue}
                size={tabSize ?? "medium"}
                value={items[activeIndex]?.value ?? ""}
              />
            </div>
          )}
        </div>
      )}

      {/* Optional app-injected geo line, centered under the tray. Out of this
          organism's scope to source — it holds no geolocation of its own. */}
      {locationSlot ? <div className={styles.location}>{locationSlot}</div> : null}

      <div className={styles.panels}>
        {panels.map((panel, index) => (
          <div
            className={styles.panel}
            /* `hidden` and nothing else: the active panel is plain content, the
               rest are removed natively. No `role="tabpanel"`/`aria-controls` —
               the tray is a radiogroup, not an APG tablist, so there is nothing
               truthful to point them at (design-conflicts). */
            hidden={index !== activeIndex}
            key={items[index]?.value ?? index}
          >
            {panel}
          </div>
        ))}
      </div>

      {/* The bottom legal disclaimer for the ACTIVE tab (its own `legal_content`,
          or the section default resolved on the server). Swaps with the selected
          tab; already rendered on the server, so the section just places it. */}
      {legals?.[activeIndex] ? (
        <div className={styles.legal}>{legals[activeIndex]}</div>
      ) : null}
    </section>
  );
}
export const HpTabCarouselShell = withSafeRender(HpTabCarouselShellImpl, "HpTabCarouselShell");

export default HpTabCarouselShell;
